import React, { useRef, useState } from 'react';
import api from '../utils/api';

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle = {
  background: 'var(--card-bg, #fff)', borderRadius: 12, width: 'min(620px, 92vw)',
  maxHeight: '85vh', overflowY: 'auto', padding: 24, boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
};
const dropStyle = (active) => ({
  border: `2px dashed ${active ? '#3A7BD5' : '#b8c2cc'}`, borderRadius: 10,
  padding: '32px 16px', textAlign: 'center', cursor: 'pointer',
  background: active ? 'rgba(58,123,213,0.06)' : 'transparent', transition: 'all .15s',
});
const btnStyle = (primary) => ({
  padding: '10px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
  fontWeight: 600, fontSize: 14,
  background: primary ? '#3A7BD5' : '#e3e8ee', color: primary ? '#fff' : '#2C4A6E',
});

const isExcel = (name) => /\.(xlsx|xlsm|xls)$/i.test(name) && !name.startsWith('~$');
const isPdf = (name) => /\.pdf$/i.test(name);

// ---- Folder traversal (drag & drop) ----
async function readEntry(entry, path, out) {
  if (entry.isFile) {
    const file = await new Promise((res, rej) => entry.file(res, rej));
    if (isExcel(file.name) || isPdf(file.name)) {
      out.push({ file, relPath: path + file.name });
    }
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    let batch;
    do {
      batch = await new Promise((res, rej) => reader.readEntries(res, rej));
      for (const child of batch) {
        await readEntry(child, path + entry.name + '/', out);
      }
    } while (batch.length > 0);
  }
}

async function filesFromDrop(dataTransfer) {
  const out = [];
  const items = Array.from(dataTransfer.items || []);
  const entries = items
    .map(i => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null))
    .filter(Boolean);
  if (entries.length) {
    for (const entry of entries) await readEntry(entry, '', out);
  } else {
    for (const f of Array.from(dataTransfer.files || [])) {
      if (isExcel(f.name) || isPdf(f.name)) out.push({ file: f, relPath: f.name });
    }
  }
  return out;
}

// ---- Group files into jobs: each Excel defines a job; PDFs in the same
// folder (or its subfolders) belong to it. Deepest matching Excel wins. ----
function groupIntoJobs(items) {
  const dirOf = (p) => p.includes('/') ? p.slice(0, p.lastIndexOf('/') + 1) : '';
  const excels = items.filter(i => isExcel(i.file.name));
  const pdfs = items.filter(i => isPdf(i.file.name));

  const jobs = excels.map(e => ({
    label: dirOf(e.relPath) ? dirOf(e.relPath).replace(/\/$/, '').split('/').pop() : e.file.name,
    dir: dirOf(e.relPath),
    excel: e,
    pdfs: [],
  }));

  const orphans = [];
  for (const p of pdfs) {
    const matches = jobs
      .filter(j => p.relPath.startsWith(j.dir))
      .sort((a, b) => b.dir.length - a.dir.length);
    if (matches.length) matches[0].pdfs.push(p);
    else if (jobs.length === 1) jobs[0].pdfs.push(p);
    else orphans.push(p);
  }
  return { jobs, orphans };
}

export default function ImportModal({ onClose, onImported }) {
  const fileRef = useRef();
  const folderRef = useRef();
  const [items, setItems] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const addItems = (newItems) => {
    setItems(prev => {
      const seen = new Set(prev.map(i => i.relPath));
      return [...prev, ...newItems.filter(i => !seen.has(i.relPath))];
    });
    setError('');
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    addItems(await filesFromDrop(e.dataTransfer));
  };

  const onPick = (e) => {
    const picked = Array.from(e.target.files)
      .filter(f => isExcel(f.name) || isPdf(f.name))
      .map(f => ({ file: f, relPath: f.webkitRelativePath || f.name }));
    addItems(picked);
    e.target.value = '';
  };

  const { jobs, orphans } = groupIntoJobs(items);

  const doImport = async () => {
    if (!jobs.length) { setError('No Excel job file found — add at least one.'); return; }
    setImporting(true);
    setError('');
    const outcomes = [];
    for (let i = 0; i < jobs.length; i++) {
      const j = jobs[i];
      setProgress(`Importing ${i + 1} of ${jobs.length}: ${j.label}…`);
      try {
        const fd = new FormData();
        fd.append('files', j.excel.file);
        j.pdfs.forEach(p => fd.append('files', p.file));
        const res = await api.post('/import', fd);
        outcomes.push({ label: j.label, ok: true, summary: res.data.summary });
      } catch (err) {
        outcomes.push({ label: j.label, ok: false, error: err.response?.data?.error || 'Import failed' });
      }
    }
    setProgress('');
    setImporting(false);
    setResults(outcomes);
  };

  const finish = () => {
    if (results && results.some(r => r.ok) && onImported) onImported();
    onClose();
  };

  return (
    <div style={overlayStyle} onClick={importing ? undefined : onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Import Jobs</h2>

        {!results && (
          <>
            <div
              style={dropStyle(dragActive)}
              onClick={() => folderRef.current.click()}
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
            >
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                Drop a job folder here — or a folder of job folders
              </div>
              <div style={{ fontSize: 13, color: '#6b7a8c' }}>
                Subfolders are searched automatically. Tap to browse for a folder,{' '}
                <span
                  style={{ color: '#3A7BD5', textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); fileRef.current.click(); }}
                >or pick individual files</span>
              </div>
              <input ref={folderRef} type="file" multiple hidden webkitdirectory="" directory="" onChange={onPick} />
              <input ref={fileRef} type="file" multiple hidden accept=".xls,.xlsx,.xlsm,.pdf" onChange={onPick} />
            </div>

            {jobs.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {jobs.map(j => (
                  <div key={j.excel.relPath} style={{ border: '1px solid #e3e8ee', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                      🏗 {j.label}
                    </div>
                    <div style={{ fontSize: 12, color: '#4a5a6c', lineHeight: 1.7 }}>
                      <div>📊 {j.excel.file.name}</div>
                      {j.pdfs.map(p => (
                        <div key={p.relPath}>
                          📄 {p.file.name}
                          {/colou?r/i.test(p.file.name)
                            ? <span style={{ color: '#9B59B6', fontWeight: 600 }}> — Colour Selection</span>
                            : <span style={{ color: '#3A7BD5' }}> — Blueprint</span>}
                        </div>
                      ))}
                      {!j.pdfs.length && <div style={{ color: '#8a97a5' }}>No PDFs found for this job</div>}
                    </div>
                  </div>
                ))}
                {orphans.length > 0 && (
                  <div style={{ fontSize: 12, color: '#c0392b', marginTop: 4 }}>
                    ⚠ {orphans.length} PDF(s) aren't inside any job folder and will be skipped:{' '}
                    {orphans.map(o => o.file.name).join(', ')}
                  </div>
                )}
              </div>
            )}

            {error && <div style={{ marginTop: 12, color: '#c0392b', fontSize: 13, fontWeight: 600 }}>{error}</div>}
            {progress && <div style={{ marginTop: 12, color: '#3A7BD5', fontSize: 13, fontWeight: 600 }}>{progress}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
              <div style={{ fontSize: 12, color: '#6b7a8c' }}>
                {jobs.length ? `${jobs.length} job(s) ready` : ''}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={btnStyle(false)} onClick={onClose} disabled={importing}>Cancel</button>
                <button style={btnStyle(true)} onClick={doImport} disabled={importing || !jobs.length}>
                  {importing ? 'Importing…' : jobs.length > 1 ? `Import ${jobs.length} Jobs` : 'Import Job'}
                </button>
              </div>
            </div>
          </>
        )}

        {results && (
          <>
            {results.map(r => (
              <div key={r.label} style={{
                border: `1px solid ${r.ok ? '#22A06B' : '#c0392b'}`,
                background: r.ok ? 'rgba(34,160,107,0.08)' : 'rgba(192,57,43,0.08)',
                borderRadius: 8, padding: 12, marginBottom: 10, fontSize: 13,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {r.ok ? '✅' : '❌'} {r.label}
                </div>
                {r.ok ? (
                  <div style={{ lineHeight: 1.7 }}>
                    <div><strong>Job #:</strong> {r.summary.job_number || '—'} &nbsp; <strong>Address:</strong> {r.summary.address || '—'}</div>
                    <div><strong>Tasks:</strong> {r.summary.tasks_created.length ? r.summary.tasks_created.join(', ') : 'None detected'}</div>
                    <div><strong>Files:</strong> {r.summary.files_attached.length ? r.summary.files_attached.join(', ') : 'None'}</div>
                  </div>
                ) : (
                  <div style={{ color: '#c0392b' }}>{r.error}</div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
              <button style={btnStyle(true)} onClick={finish}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
